/**
 * SEO helper component using react-helmet-async.
 *
 * Injects `<title>`, meta description, canonical URL, Open Graph tags,
 * Twitter Card tags, and optional JSON-LD structured data into the
 * document `<head>`.
 *
 * @module SEO
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

/** Props accepted by the SEO component. */
interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
  keywords?: string;
  /** Map of language code → path for hreflang alternate links (e.g. { en: '/blog/my-post', nl: '/blog/my-post' }). */
  hreflangMap?: Record<string, string>;
}

const SITE_NAME = 'Booge IT';
const DEFAULT_DESCRIPTION =
  'Booge IT builds custom apps and websites for Android, iOS, Windows, macOS, Linux & Web. AI-powered, fast delivery, tailored to your business.';
const BASE_URL = 'https://www.booge-it.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

/** Map language codes to og:locale values. */
const LOCALE_MAP: Record<string, string> = {
  nl: 'nl_NL',
  en: 'en_US',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
};

/**
 * Renders SEO meta tags and structured data for a page.
 * Falls back to translated defaults when title or description are omitted.
 */
export default function SEO({
  title,
  description,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
  keywords,
  hreflangMap,
}: SEOProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'nl';
  const siteName = t('seo.siteName');
  const fullTitle = title ? `${title} | ${siteName}` : t('seo.defaultTitle');
  const desc = description || t('seo.defaultDescription');
  const url = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const ogLocale = LOCALE_MAP[lang] || 'nl_NL';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternate links for multilingual SEO */}
      {hreflangMap &&
        Object.entries(hreflangMap).map(([hrefLang, path]) => (
          <link
            key={hrefLang}
            rel="alternate"
            hrefLang={hrefLang}
            href={`${BASE_URL}${path}`}
          />
        ))}
      {hreflangMap && (
        <link rel="alternate" hrefLang="x-default" href={url} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {hreflangMap &&
        Object.keys(hreflangMap)
          .filter((l) => l !== lang)
          .map((l) => (
            <meta
              key={`og-alt-${l}`}
              property="og:locale:alternate"
              content={LOCALE_MAP[l] || l}
            />
          ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
